import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import {
  LogOut, BarChart3, Users, Activity, Gauge, AlertTriangle, Save,
  CheckCircle, Sparkles, Server, RefreshCw, ArrowRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

const COST_TYPES = [
  { value: 'all', label: 'All Costs' },
  { value: 'infrastructure', label: 'Cloud Infrastructure' },
  { value: 'ai', label: 'AI Usage' }
];

function StatCard({ title, value, icon: Icon, accent }) {
  return (
    <div className="dashboard-card !p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent || 'text-accent-dark'}`} />
        <span className="text-xs text-slate-500">{title}</span>
      </div>
      <div className="text-2xl font-bold text-primary">{value}</div>
    </div>
  );
}

export default function TelemetryPage() {
  const [overview, setOverview] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [costType, setCostType] = useState('all');
  const { logout } = useAuth();

  const load = useCallback(async (s = startDate, e = endDate, ct = costType) => {
    setLoading(true);
    try {
      const params = {};
      if (s) params.start = s;
      if (e) params.end = e;
      const [ov, co] = await Promise.all([
        api.get('/api/admin/telemetry/overview', { params }),
        api.get('/api/admin/telemetry/costs', { params: { ...params, cost_type: ct } })
      ]);
      setOverview(ov.data);
      setCosts(co.data);
    } catch (err) {
      toast.error('Failed to load telemetry');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => load(startDate, endDate, costType);

  if (loading && !overview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
            <div>
              <h1 className="font-semibold text-primary">Usage & Performance Telemetry</h1>
              <p className="text-xs text-slate-500">Platform utilization, infrastructure & AI cost estimates</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm" data-testid="dashboard-link">
              <BarChart3 className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/admin/console" className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm" data-testid="console-link">
              <Users className="w-4 h-4" /> Members
            </Link>
            <button onClick={logout} className="text-slate-500 hover:text-slate-700 p-2" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto space-y-6">
        {/* Filters */}
        <div className="dashboard-card !p-4 flex flex-wrap items-end gap-4" data-testid="telemetry-filters">
          <div>
            <label className="block text-xs text-slate-500 mb-1">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="start-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="end-date-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Cost Type</label>
            <select value={costType} onChange={e => setCostType(e.target.value)}
              className="h-10 px-3 rounded-xl border border-slate-200 text-sm bg-white" data-testid="cost-type-select">
              {COST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button onClick={applyFilters} className="btn-accent h-10 px-5 flex items-center gap-2 text-sm" data-testid="apply-filters-btn">
            <RefreshCw className="w-4 h-4" /> Apply
          </button>
          <span className="text-xs text-slate-400 ml-auto">
            Period: {overview?.period?.start} → {overview?.period?.end}
          </span>
        </div>

        {/* Utilization stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="utilization-stats">
          <StatCard title="API Requests" value={(overview?.api_requests ?? 0).toLocaleString()} icon={Activity} />
          <StatCard title="Avg Latency" value={`${overview?.avg_latency_ms ?? 0} ms`} icon={Gauge} />
          <StatCard title="P95 Latency" value={`${overview?.p95_latency_ms ?? 0} ms`} icon={Gauge} accent="text-amber-500" />
          <StatCard title="Error Rate" value={`${overview?.error_rate_pct ?? 0}%`} icon={AlertTriangle} accent="text-red-500" />
          <StatCard title="Member Logins" value={overview?.member_logins ?? 0} icon={Users} accent="text-blue-500" />
          <StatCard title="Surveys Active" value={overview?.responses_active ?? 0} icon={Save} />
          <StatCard title="Submissions" value={overview?.responses_submitted ?? 0} icon={CheckCircle} accent="text-emerald-500" />
          <StatCard title="AI Calls" value={overview?.ai_calls ?? 0} icon={Sparkles} accent="text-purple-500" />
        </div>

        {/* Requests chart + top endpoints */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="dashboard-card" data-testid="requests-chart">
            <h2 className="text-lg font-semibold text-primary mb-4">API Requests per Day</h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={overview?.requests_daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Bar dataKey="value" name="Requests" fill="#40D39B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="dashboard-card" data-testid="top-endpoints">
            <h2 className="text-lg font-semibold text-primary mb-4">Top Endpoints</h2>
            {(overview?.top_endpoints || []).length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center">Telemetry collection just started — data accumulates from now on</p>
            ) : (
              <div className="space-y-2">
                {overview.top_endpoints.map((e) => (
                  <div key={e.endpoint} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <code className="text-xs text-slate-600">{e.endpoint}</code>
                    <span className="text-sm font-semibold text-primary">{e.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cost calculator */}
        <div className="dashboard-card" data-testid="cost-calculator">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                <Server className="w-5 h-5 text-accent-dark" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-primary">Estimated Cost Calculator (to date)</h2>
                <p className="text-xs text-slate-500">{costs?.disclaimer}</p>
              </div>
            </div>
            <Link to="/admin/ai-transparency" className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm" data-testid="ai-transparency-link">
              <Sparkles className="w-4 h-4 text-purple-500" /> AI Transparency <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr><th>Category</th><th>Cost Item</th><th>Basis</th><th className="text-right">Est. Cost (USD)</th></tr>
              </thead>
              <tbody>
                {(costs?.items || []).map((item, i) => (
                  <tr key={i} data-testid={`cost-item-${i}`}>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.category === 'ai' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {item.category === 'ai' ? 'AI' : 'Infrastructure'}
                      </span>
                    </td>
                    <td className="font-medium">{item.item}</td>
                    <td className="text-sm text-slate-500">{item.detail}</td>
                    <td className="text-right font-mono font-semibold">${item.cost.toFixed(item.category === 'ai' ? 4 : 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap justify-end gap-6 mt-4 pt-4 border-t border-slate-200 text-sm" data-testid="cost-totals">
            <div>Infrastructure: <span className="font-bold text-primary">${(costs?.totals?.infrastructure ?? 0).toFixed(2)}</span></div>
            <div>AI: <span className="font-bold text-purple-700">${(costs?.totals?.ai ?? 0).toFixed(4)}</span></div>
            <div className="text-base">Grand Total: <span className="font-bold text-primary">${(costs?.totals?.grand_total ?? 0).toFixed(2)}</span></div>
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
